import { useEffect } from 'react';
import { Sheet } from 'react-modal-sheet';
import { Virtuoso } from 'react-virtuoso';
import { InputPlain } from '@peacecover/component';
import { Chat, LikeFill, LikeLine, Xmark } from '@peacecover/icon';
import { useIOSKeyboardSheetPadding } from '../../../hooks/useIOSKeyboardSheetPadding';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import { useGetUserInfo } from '../../../domains/user/ui/hooks/useGetUserInfo';
import { useCreateComment } from '../hooks/useCreateComment';
import { useManageCommentBottomSheet } from '../hooks/useManageCommentBottomSheet';
import { useGetAdaptedFictionEditionComments } from '../../../domains/scene/ui/hooks/useGetAdaptedFictionEditionComments';
import { openNewTab } from '../../../utils/UIUtils';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import Anonymous from '../../../icons/Anonymous';
import type { components } from '../../../types/schema';

interface CommentBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onKeyboardVisibleChange?: (visible: boolean) => void;
  adaptedFictionEditionId: string;
  adaptedFictionPreviewId: string;
  /** true일 때 시트를 화면 하단(bottom-0)에 고정 */
  snapToBottom?: boolean;
}

const CommentBottomSheet = ({
  isOpen,
  onClose,
  onKeyboardVisibleChange,
  adaptedFictionEditionId,
  adaptedFictionPreviewId,
  snapToBottom = false
}: CommentBottomSheetProps) => {
  const { containerRef } = useIOSKeyboardSheetPadding({ enabled: isOpen });
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();
  const {
    data: commentPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGetAdaptedFictionEditionComments({
    adaptedFictionEditionId,
    params: {
      per_page: 10
    }
  });
  const flattenedComments = commentPages?.pages.flatMap((page) => page.comments ?? []) ?? [];
  const hasComments = flattenedComments.length > 0;
  const {
    comment,
    setComment,
    isAnonymous,
    setIsAnonymous,
    handleCreateAdaptedFictionEditionComment
  } = useCreateComment({
    adaptedFictionEditionId,
    adaptedFictionPreviewId
  });
  const { data: user } = useGetUserInfo();

  useEffect(() => {
    onKeyboardVisibleChange?.(isKeyboardVisible);
  }, [isKeyboardVisible, onKeyboardVisibleChange]);

  useEffect(() => {
    if (!isOpen) {
      onKeyboardVisibleChange?.(false);
    }
  }, [isOpen, onKeyboardVisibleChange]);

  const handleInputBlur = () => {
    handleBlur();
    onKeyboardVisibleChange?.(false);
  };

  const sheetBottomClass =
    snapToBottom || isKeyboardVisible
      ? '!bottom-0'
      : '!bottom-[calc(70px+env(safe-area-inset-bottom))]';

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content" className={sheetBottomClass}>
      <Sheet.Container className="!rounded-t-[20px] pt-[32px] dark:!bg-background-elevated !pb-0">
        <Sheet.Content ref={containerRef} className="overflow-hidden">
          <div className="flex flex-col">
            <div className={hasComments ? 'h-[60vh] min-h-[240px]' : 'h-[100px]'}>
              <Virtuoso
                data={flattenedComments}
                className="hiddenScrollBar"
                defaultItemHeight={120}
                style={{ height: '100%' }}
                endReached={() => {
                  if (!hasNextPage || isFetchingNextPage) return;
                  fetchNextPage();
                }}
                overscan={200}
                itemContent={(
                  _index,
                  item: components['schemas']['ApiV1AdaptedFictionEditionComment']
                ) => (
                  <CommentItem
                    comment={item}
                    adaptedFictionEditionId={adaptedFictionEditionId}
                    adaptedFictionPreviewId={adaptedFictionPreviewId}
                  />
                )}
              />
            </div>
            <div className="px-[22px] pt-[16px] pb-[32px] flex items-center gap-[10px] border-t border-black-opacity-50">
              <div className="relative w-[34px] h-[34px]">
                {isAnonymous ? (
                  <Anonymous size={34} className="flex-shrink-0" />
                ) : (
                  <ProfileAvatar
                    thumbnailUrl={user?.baseUserProfile?.thumbnailUrl}
                    widthAndHeight="34px"
                    className="flex-shrink-0"
                  />
                )}
                <button
                  type="button"
                  className="absolute bottom-0 right-[-2px] w-[12px] h-[12px] rounded-full bg-primary-light dark:bg-primary-dark border border-pure-white dark:border-pure-black"
                  onClick={() => setIsAnonymous(!isAnonymous)}
                />
              </div>
              <div className="py-[8px] w-full flex items-center justify-between gap-[10px]">
                <InputPlain
                  size="small"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleInputBlur}
                  placeholder="여기에 감상평을 작성해보세요."
                  className="bg-transparent font-semibold"
                />
                {comment.trim() && (
                  <button
                    type="button"
                    className="flex-shrink-0 text-[15px] font-semibold leading-[18px] text-primary-light dark:text-primary-dark"
                    onClick={() => handleCreateAdaptedFictionEditionComment()}
                  >
                    전송
                  </button>
                )}
              </div>
            </div>
          </div>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-transparent" />
    </Sheet>
  );
};

export default CommentBottomSheet;

const CommentItem = ({
  comment,
  adaptedFictionEditionId,
  adaptedFictionPreviewId
}: {
  comment: components['schemas']['ApiV1AdaptedFictionEditionComment'];
  adaptedFictionEditionId: string;
  adaptedFictionPreviewId: string;
}) => {
  const { handleLikeComment, handleFollowUser } = useManageCommentBottomSheet({
    adaptedFictionEditionId,
    commentId: comment.commentId
  });

  return (
    <div className="px-[22px] py-[16px] flex items-start gap-[10px]">
      <button
        type="button"
        className="w-[34px] h-[34px]"
        onClick={() => openNewTab(`/feed/user_profile/${comment.userProfileId}`)}
      >
        {comment.anonymous ? (
          <Anonymous size={34} />
        ) : (
          <ProfileAvatar thumbnailUrl={comment.authorThumbnailUrl} widthAndHeight="34px" />
        )}
      </button>
      <div className="flex flex-col items-start w-full">
        <div className="flex items-end justify-between w-full pt-[1px] pb-[5px]">
          <div className="flex items-end gap-[6px]">
            <p className="text-[14px] font-semibold leading-[17px] text-text-main-lv2">
              {comment.authorName}
            </p>
            <p className="text-[12px] leading-[14px] text-text-main-lv4">{comment.authoredAt}</p>
          </div>
          {!comment.isFollowing && !comment.isMine && (
            <button
              type="button"
              className="text-[12px] leading-[14px] font-medium text-primary-light dark:text-primary-dark"
              onClick={() => handleFollowUser(comment.userProfileId ?? '')}
            >
              팔로우+
            </button>
          )}
        </div>
        {comment.castRelationship && (
          <div className="px-[6px] py-[4px] rounded-[4px] bg-surface-lv2 flex items-center gap-[5px] text-text-main-lv4">
            <p className="text-[12px] font-medium leading-[14px]">
              {comment.castRelationship?.personAName}
            </p>
            <Xmark size={12} />
            <p className="text-[12px] font-medium leading-[14px]">
              {comment.castRelationship?.personBName}
            </p>
          </div>
        )}
        <p className="text-[15px] leading-[19px] text-text-main-lv2 pb-[3px] mt-[4px]">
          {comment.content}
        </p>
        <div className="py-[5px] flex items-center gap-[24px]">
          <button
            type="button"
            className="flex items-center gap-[4px]"
            onClick={() =>
              openNewTab(
                `/scene/${adaptedFictionEditionId}/comments/${comment.commentId}?adaptedFictionPreviewId=${adaptedFictionPreviewId}`
              )
            }
          >
            <Chat size={18} className="text-text-sub-lv1" />
            <p className="text-[15px] font-medium leading-[18px] text-text-sub-lv1">
              {comment.commentsCount}
            </p>
          </button>
          <button
            type="button"
            className={`flex items-center gap-[4px] ${comment.isLiked ? 'text-warning' : 'text-text-sub-lv1'}`}
            onClick={handleLikeComment}
          >
            {comment.isLiked ? <LikeFill size={18} /> : <LikeLine size={18} />}
            <p className="text-[15px] font-medium leading-[18px]">{comment.likesCount}</p>
          </button>
        </div>
      </div>
    </div>
  );
};
